import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  Button,
  Fade,
  Zoom,
  Slide
} from '@mui/material';
import { Security, ArrowBack, Refresh } from '@mui/icons-material';

const AuthRequired = () => {
  const handleGoToCodeLens = () => {
    window.location.href = 'https://codelens.epoch.co/';
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'float 20s linear infinite',
          zIndex: 0
        }}
      />
      
      <Fade in timeout={1000}>
        <Card 
          sx={{ 
            maxWidth: 500, 
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            zIndex: 1
          }}
        >
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Slide direction="up" in timeout={800}>
              <Box sx={{ mb: 4 }}>
                <Zoom in timeout={1000}>
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                      animation: 'pulse 2s infinite',
                      mx: 'auto'
                    }}
                  >
                    <Security sx={{ fontSize: 50, color: 'white' }} />
                  </Box>
                </Zoom>
              </Box>
            </Slide>
            
            <Fade in timeout={1200}>
              <Box>
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 3
                  }}
                >
                  Authentication Required
                </Typography>
                
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.6 }}
                >
                  Please authenticate through the main CodeLens application to access the Compliance security dashboard.
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 4, fontSize: '1rem', lineHeight: 1.5 }}
                >
                  This application uses centralized authentication. You need to be logged in through the main CodeLens platform to access this security dashboard.
                </Typography>
                
                <Fade in timeout={1500}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<ArrowBack />}
                      onClick={handleGoToCodeLens}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                          boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Go to CodeLens Authentication
                    </Button>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        cursor: 'pointer', 
                        '&:hover': { 
                          textDecoration: 'underline',
                          color: 'primary.main'
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                      onClick={handleRetry}
                    >
                      <Refresh sx={{ fontSize: 16 }} />
                      Clear session and try again
                    </Typography>
                  </Box>
                </Fade>
              </Box>
            </Fade>
          </CardContent>
        </Card>
      </Fade>
      
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        @keyframes float {
          0% { transform: translateY(0px) rotate(0deg); }
          100% { transform: translateY(-20px) rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default AuthRequired;
